import { Icon } from '@openeducation/oe-rn-components';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Animated,
  Platform,
  TouchableOpacity,
  TouchableOpacityProps,
  View,
  ViewStyle,
  Image,
} from 'react-native';

import { ChoiceBubbleStatus } from './choice-bubble-status.enum';
import {
  correctAnimation,
  incorrectAnimation,
} from './choice-bubble.animations';
import styles from './choice-bubble.style';
import { useColors } from '../../hooks';
import { useKeyPress } from '../../hooks/use-key-press';
import { useSound } from '../../hooks/use-sound';
import { mediaQueryValues } from '../media-query';
import { ThemedText } from '../themed-text';

interface ChoiceBubbleProps extends TouchableOpacityProps {
  text: string;
  status: ChoiceBubbleStatus;
  optionKey?: string;
  audioUrl?: string;
  disabled?: boolean;
  isImage?: boolean;
  buttonStyle?: ViewStyle | ViewStyle[];
  style?: ViewStyle | ViewStyle[];
  onPress?: () => void;
}

export function ChoiceBubble({
  text,
  status,
  optionKey,
  audioUrl,
  onPress,
  isImage = false,
  disabled = false,
  buttonStyle,
  style,
  ...props
}: ChoiceBubbleProps) {
  const theme = useColors();
  const mediaQuery = mediaQueryValues();
  const [x] = useState(new Animated.Value(0));
  const [s] = useState(new Animated.Value(0));
  const keyPress = useKeyPress(optionKey);
  const audio = useSound({ url: audioUrl });

  const onPressWithAudio = useCallback(() => {
    if (audio?.status?.isLoaded) {
      audio.setProgress(0);
      audio.play();
    }
    if (onPress) {
      onPress();
    }
  }, [audio, onPress]);

  useEffect(() => {
    if (
      status !== ChoiceBubbleStatus.SELECTED &&
      audio?.status?.isLoaded &&
      audio?.status?.isPlaying
    ) {
      audio.pause();
    }
  }, [status, audio]);

  useEffect(() => {
    if (keyPress && !disabled) {
      onPressWithAudio();
    }
  }, [keyPress, onPressWithAudio, disabled]);

  useEffect(() => {
    if (status === ChoiceBubbleStatus.CORRECT) {
      correctAnimation(s).start();
    } else if (status === ChoiceBubbleStatus.INCORRECT) {
      incorrectAnimation(x).start();
    }

    return () => {
      correctAnimation(s).reset();
      incorrectAnimation(x).reset();
    };
  }, [x, s, status]);

  let containerStyle;
  let textStyle;

  switch (status) {
    case ChoiceBubbleStatus.SELECTED:
      containerStyle = {
        borderColor: theme.primary,
      };
      textStyle = { color: theme.primary };
      break;
    case ChoiceBubbleStatus.CORRECT:
      containerStyle = {
        borderColor: theme.success,
      };
      break;
    case ChoiceBubbleStatus.INCORRECT:
      containerStyle = { borderColor: theme.error };
      break;
    default:
      containerStyle = {
        borderColor: theme.grayLight,
      };
      textStyle = { color: theme.onBackground };
  }

  if (!onPress) {
    containerStyle = { borderColor: theme.gray, opacity: 0.4 };
    textStyle = { color: theme.onBackground };
  }

  const translateX = x.interpolate({
    inputRange: [-1, 0, 1],
    outputRange: [-5, 0, 5],
  });

  const scale = s.interpolate({ inputRange: [0, 1], outputRange: [1, 1.1] });

  return (
    <TouchableOpacity
      onPress={onPressWithAudio}
      disabled={!onPress || disabled}
      {...props}
    >
      <Animated.View
        style={[
          styles.container,
          { transform: [{ translateX }, { scale }] },
          style,
        ]}
      >
        <View
          style={[
            styles.button,
            { backgroundColor: theme.background },
            containerStyle,
            buttonStyle,
          ]}
        >
          {audio?.status?.isLoaded && (
            <Icon
              color={textStyle?.color || theme.gray}
              size={mediaQuery.small}
              name="volume-up"
              type="solid"
              style={styles.audioIcon}
            />
          )}
          {!isImage && (
            <ThemedText type="onBackground" style={[styles.text, textStyle]}>
              {text}
            </ThemedText>
          )}
          {isImage && <Image style={[styles.image]} source={{ uri: text }} />}
        </View>
        {Platform.OS === 'web' && !disabled && optionKey && !isImage && (
          <View
            style={[
              styles.optionKeyContainer,
              { backgroundColor: theme.background },
              containerStyle,
            ]}
          >
            <ThemedText
              type="onBackground"
              fontSize="extraSmall"
              style={textStyle}
            >
              {optionKey}
            </ThemedText>
          </View>
        )}
      </Animated.View>
    </TouchableOpacity>
  );
}
